/*
 * Join the elements in an javascript array,
 * but let the last separator be different eg: `and` / `or`
 * Stackoverflow link: http://stackoverflow.com/questions/15069587/is-there-a-way-to-join-the-elements-in-an-js-array-but-let-the-last-separator-b
 * Credit: Chris Barr - http://stackoverflow.com/users/79677/chris-barr
 */
import { isString, isEmpty, snakeCase, isBoolean, isNumber } from 'lodash';
import ordinal from 'ordinal';

export const concatString = (arr, separator = ',', lastSeparator) => {
  let outStr = '';
  if (arr.length === 2) {
    // joins all with "and" but no commas
    // example: "bob and sam"
    outStr = arr.join(lastSeparator || separator);
  } else if (arr.length > 2 && lastSeparator) {
    // joins all with commas, but last one gets ", and" (oxford comma!)
    // example: "bob, joe, and sam"
    outStr = `${arr
      .slice(0, -1)
      .join(separator)}${separator}${lastSeparator}${arr.slice(-1)}`;
  } else {
    outStr = arr.join(separator);
  }
  return outStr;
};

export const stripHtmlTags = original => original.replace(/<[^>]+>/g, '');

export const stripNonDigits = original => original.replace(/\D/g, '');

export const stripNonAlpha = original => original.replace(/[^A-Z]/gi, '');

export const stripNewLines = original => (original || '').replace(/\n|\r/g, '');

export const capitalize = (str, lower = false) =>
  (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, match =>
    match.toUpperCase(),
  );

export const possessiveStudentName = (firstName, lastName) =>
  lastName.slice(-1) === 's'
    ? `${firstName} ${lastName}' `
    : `${firstName} ${lastName}'s `;

export const possessiveName = name => {
  if (!name || typeof name !== 'string') {
    return '';
  }

  return name.slice(-1) === 's' ? `${name}' ` : `${name}'s `;
};

export const formatGradeAndSchool = (gradeYear, schoolName) => {
  if (!gradeYear || !schoolName) {
    return null;
  }

  return `${ordinal(gradeYear)} grade, ${schoolName}`;
};

export const getAbbreviation = str => str.match(/\b\w/g).join('');

export const truncateMiddleWithEllipsis = (
  string = '',
  startStrLength = 0,
  endStrLength = startStrLength,
) => {
  if (!isString(string)) {
    console.warn('Must pass a string');
    return '';
  }
  return `${string.slice(0, startStrLength)}...${string.slice(-endStrLength)}`;
};

export const getArticleForString = str => {
  if (!str || typeof str !== 'string') {
    return '';
  }

  const vowels = ['a', 'e', 'i', 'o', 'u'];
  const firstLetter = str[0].toLowerCase();

  if (vowels.includes(firstLetter)) {
    return 'an';
  }
  return 'a';
};

export const formatFullName = (firstName, lastName) => {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  if (firstName) {
    return `${firstName}`;
  }
  return `${lastName}`;
};

/* Returns comma-separated list with "and" before final item if list is longer than two items
 * ex. list = [6, 7, 8] => "6, 7, and 8" */
export const formatCommaListWithAnd = list =>
  list.join(', ').replace(/, ((?:.(?!, ))+)$/, ' and $1');

export const ensureString = (str, defaultValue = '') => {
  if (isString(str)) {
    return str;
  }

  if (isBoolean(str) || isNumber(str)) {
    return String(str);
  }

  return defaultValue;
};

/* Since booleans are converted to strings when retrieving via events, use this function to convert it back to a boolean */
export const stringToBoolean = str => {
  if (str.toLowerCase() === 'true') {
    return true;
  }
  if (str.toLowerCase() === 'false') {
    return false;
  }
  return str;
};

export const hasImageExtension = ({ fileName = '' }) => {
  if (!fileName) {
    return false;
  }
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff'];
  // eslint-disable-next-line no-restricted-syntax
  for (const ext of imageExtensions) {
    if (fileName.endsWith(ext)) {
      return true;
    }
  }
  return false;
};

export const hasVideoExtension = ({ fileName = '' }) => {
  if (!fileName) {
    return false;
  }
  const videoExtensions = [
    '.mp4',
    '.mov',
    '.avi',
    '.mkv',
    '.wmv',
    '.flv',
    '.webm',
  ];
  // eslint-disable-next-line no-restricted-syntax
  for (const ext of videoExtensions) {
    if (fileName.endsWith(ext)) {
      return true;
    }
  }
  return false;
};

/**
 * @param c {bool} - If true, will append the value of app
 * @param base {string} - This string will always be shown
 * @param app {string} - The string to append after base
 * @returns {string}
 */
export const conditionalAppend = (c, base, app) => `${base}${c ? app : ''}`;

/**
 * @param condition {bool} - If true, will prepend the value of app
 * @param base {string} - This string will always be shown
 * @param pre {string} - The string to prepend before base
 * @returns {string}
 */
export const conditionalPrepend = (condition, base, pre) =>
  `${condition ? pre : ''}${base}`;

export const formatCityState = (locationObj = {}) => {
  const { city, state } = locationObj;
  if (city && state) {
    return `${city}, ${state}`;
  }
  if (city) {
    return city;
  }
  if (state) {
    return state;
  }
  return '';
};

/**
 * Handle special key conversion where it's already
 * a snake case but with double underscore]
 *
 * this is coming from django query syntax e.g.
 * field__gte=3
 * if using snakeCase from lodash, it will only keep one _
 * and will result with field_gte=3
 *
 * @param str {string} - incoming string
 * @returns {string}
 */
export const customSnakeCase = str => {
  const parts = str.split(/(_+)/);

  const processedParts = parts.map(part => {
    if (part.includes('_')) {
      return part;
    }
    return snakeCase(part);
  });
  return processedParts.join('');
};

export const valueOrDash = value => (isEmpty(value) ? '-' : value);

export const addPeriod = (str = '') => (str.endsWith('.') ? str : `${str}.`);

export const minLengthString = ({ str = '', len = 2 }) => {
  if (!isString(str)) {
    return '';
  }

  if (str.length < len) {
    return '';
  }

  return str;
};

/**
 * Converts a camelCase string to a hyphen-separated (kebab-case) string.
 *
 * @param {string} str - The camelCase string to convert.
 * @returns {string} The converted kebab-case string.
 *
 * @example
 * camelToHyphen('myVariableName'); // Returns 'my-variable-name'
 */
export const camelCaseToKebabCase = str =>
  ensureString(str)
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase();
